import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetMessagesUpdatesStatsQuery,
  GetMessagesUpdatesStatsData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetMessagesUpdatesStatsState
  extends MergeRequestState<GetMessagesUpdatesStatsData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getMessagesUpdatesStats,
  isFetching: (state: ReduxState) =>
    state.server.getMessagesUpdatesStats.isFetching,
  data: (state: ReduxState) => state.server.getMessagesUpdatesStats.data,
  error: (state: ReduxState) => state.server.getMessagesUpdatesStats.error,
};

const initialState: GetMessagesUpdatesStatsState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getMessagesUpdatesStats';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      query,
      config,
    }: { query: GetMessagesUpdatesStatsQuery; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getMessagesUpdatesStats(query, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetMessagesUpdatesStats {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getMessagesUpdatesStats: ServerGetMessagesUpdatesStats = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
